import React, { KeyboardEvent, RefObject, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Select } from '../../forms/Select';
import { SelectDropdownTemplate, SelectItem } from '../../forms/Select/Select.view';
import { RegisterOptions, UseFormReturn, useWatch } from 'react-hook-form';
import { FormElementValueType } from '../../../models/basic-objects';
import { ListItemContainer } from '../../containers/ListItemContainer';
import { ListItem } from '../../actions/ListItem';
import { Button } from '../../actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import classes from './PinterestBoard.module.scss';
import classNames from 'classnames';
import { BreakpointsValuesEnum } from '../../../constants/breakpoints-values';
import { useDimensions } from '../../../hooks/useDimensions';
import { capitalize } from 'lodash';
import { Input } from '../../forms/Input';
import { SquareButtonIconsEnum } from 'constants/square-button-icons';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { PostEditorContext } from 'modules/PostEditor/states/PostEditorProvider';
import { PostEditorGroupDataContext } from 'modules/PostEditor/states/PostEditorGroupDataProvider';
import { profilesHelpers } from 'modules/Settings/Profiles/Profiles.helpers';
import { usePrevious } from 'primereact/hooks';
import { SocialProfileComponentExtendedType } from '../SocialProfile/SocialProfile.view';
import { ErrorMessageSeverity } from 'constants/form';
import { hasSeverity } from 'modules/PostEditor/validators/helpers/severity-checker';

export type PinterestBoardViewProps = {
  profile: SocialProfileComponentExtendedType;
  selectItems: SelectItem[];
  useFormInstance: UseFormReturn;
  pinterestBoardValue?: FormElementValueType;
  pinterestFormName: string;
  pinterestRules: RegisterOptions;
  refreshPnBoards: VoidFunction;
  loading: boolean;
  parentDropdownRef?: RefObject<HTMLDivElement | null>;
  isParentDropdownExpanded: boolean;
};

type FilteredItemsProps = {
  items: Array<string | SelectItem>;
  expanded: boolean;
  onClickList: (item: SelectItem | string) => void;
  selectedValue: FormElementValueType;
};

const FilteredItems = (props: FilteredItemsProps) => {
  const { items, expanded, onClickList, selectedValue } = props;

  return items.length ? (
    <ListItemContainer type={'div'} expanded={expanded} position={'down'}>
      {items.map((item, index) => (
        <ListItem
          key={`${typeof item === 'string' ? item : item.label}-${index}`}
          role={'option'}
          aria-label={typeof item === 'string' ? item : item.label}
          variation={'regular'}
          type={'div'}
          selected={typeof item !== 'string' && selectedValue ? item.value === selectedValue : false}
          action={() => {
            onClickList(item);
          }}
          disabled={false}>
          {typeof item === 'string' ? item : item.label}
        </ListItem>
      ))}
    </ListItemContainer>
  ) : (
    <div className={classes.NoBoardsSearch}>No Boards Found</div>
  );
};

const dropdownTemplateGenerator = (
  refreshPnBoards: PinterestBoardViewProps['refreshPnBoards'],
  loading: boolean
): SelectDropdownTemplate => {
  const DropdownTemplate: SelectDropdownTemplate = (props, globalClasses): JSX.Element => {
    const { setExpanded, useFormInstance, name, items, expanded, selectedValue } = props;
    const [filteredItems, setFilteredItems] = useState<Array<string | SelectItem>>(items);
    const documentRef = useRef(document.body);
    const dropDownRef = useRef<HTMLDivElement | null>(null);
    const formInstanceRef = useRef(useFormInstance);
    const { height } = useDimensions(documentRef);
    const verticalType = height > BreakpointsValuesEnum.tabletportrait ? 'desktop' : 'mobile';
    const drodpwonClasses = classNames(globalClasses, classes.PinterestBoardContainerWrapper, {
      [classes[`PinterestBoardContainerWrapper${capitalize(verticalType)}`]]: expanded
    });

    const inputFilterWatchValue = useWatch({
      control: useFormInstance.control,
      name: `pn-search-item-${name}`
    });

    // if we close the selector we want to make sure we reset the filter
    useEffect(() => {
      if (!expanded && inputFilterWatchValue) {
        formInstanceRef.current.setValue(`pn-search-item-${name}`, '');
        setFilteredItems(items);
      }
    }, [name, expanded, inputFilterWatchValue, items]);

    // if we refreshed the boards and get new items, we want to reset the search to initial
    useEffect(() => {
      formInstanceRef.current.setValue(`pn-search-item-${name}`, '');
      setFilteredItems(items);
    }, [items, name]);

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    };

    const filterItems = (value: string) => {
      setFilteredItems(_ => {
        if (!value) return items;
        return items.flatMap(item => {
          if (typeof item === 'string') return item;
          return item.label.toLowerCase().includes(value.toLowerCase()) ? item : [];
        });
      });
    };
    const onClickList = (item: SelectItem | string) => {
      const selectValue = typeof item === 'string' ? item : item.value;
      setExpanded(prevState => !prevState);
      useFormInstance.setValue(name, selectValue);
    };

    return (
      <div className={drodpwonClasses} ref={dropDownRef}>
        {items.length ? (
          <>
            <Input
              placeholder={'Search for board'}
              onKeyDown={onKeyDown}
              updateAction={(value, complete) => {
                complete(true);
                if (typeof value !== 'string') return;
                filterItems(value);
              }}
              {...(Boolean(inputFilterWatchValue) && {
                actionButton: {
                  icon: SquareButtonIconsEnum.closeSmall,
                  disabled: !inputFilterWatchValue
                },
                action: () => {
                  if (inputFilterWatchValue) {
                    useFormInstance.setValue(`pn-search-item-${name}`, '');
                    filterItems('');
                  }
                }
              })}
              inputWrapperClassName={classes.PinterestBoardSearchWrapper}
              formInstance={useFormInstance}
              name={`pn-search-item-${name}`}
              updateDebounceTiming={150}
              rules={{}}
              size={'medium'}
            />
            <FilteredItems
              items={filteredItems}
              expanded={expanded}
              onClickList={onClickList}
              selectedValue={selectedValue}
            />
          </>
        ) : (
          <div className={classes.NoPinterestBoards}>
            You don&apos;t have any Pinterest Board within you Account. Please add a Board to be able to publish.
          </div>
        )}
        <div className={classes.PinterestBoardButtonWrapper}>
          <Button
            type={'button'}
            variant={'hollow'}
            size={'medium'}
            loading={loading}
            color={ButtonColorsEnum.primary}
            expanded={true}
            action={() => refreshPnBoards()}>
            Refresh Boards
          </Button>
        </div>
      </div>
    );
  };

  return DropdownTemplate;
};

export const PinterestBoardView = (props: PinterestBoardViewProps): JSX.Element => {
  const {
    selectItems,
    useFormInstance,
    pinterestBoardValue,
    pinterestFormName,
    pinterestRules,
    refreshPnBoards,
    loading,
    parentDropdownRef,
    profile,
    isParentDropdownExpanded
  } = props;
  const profileId = profile.id;
  const { state, profiles } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const postGroup = state.postGroups[postGroupIndex];

  const selectedProfiles = postGroup.profiles;
  const stringifiedSelectedProfilesIds = JSON.stringify(selectedProfiles.map(profile => profile.profileId) ?? []);
  const previousSelectedProfilesValue = usePrevious(stringifiedSelectedProfilesIds);
  const firstProfileInvalid = profilesHelpers.getFirstProfileInvalid(selectedProfiles);

  // TODO: Merge this logic into a single place when we move this to the cog icon
  const scrollToFirstInvalid = useCallback(() => {
    if (firstProfileInvalid && firstProfileInvalid?.profileId === profileId) {
      const profilesHTMLNodes =
        parentDropdownRef?.current?.firstElementChild?.lastElementChild?.lastElementChild?.children;
      const indexProfile = profiles.findIndex(profile => profile.id === firstProfileInvalid.profileId);
      const profileToScroll = profilesHTMLNodes?.item(indexProfile);
      const container = parentDropdownRef?.current;
      if (profileToScroll && container) {
        const elementRect = profileToScroll.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const isElementInView = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

        if (!isElementInView) {
          profileToScroll.scrollIntoView({
            // type definition bugged. We need to upgrade typescript More info: https://github.com/microsoft/TypeScript/issues/46654
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            behavior: 'instant',
            block: 'nearest'
          });
        }
      }
    }
  }, [firstProfileInvalid, parentDropdownRef, profiles, profileId]);

  const shouldEnterScrollLogic =
    selectedProfiles.length === profiles.length &&
    previousSelectedProfilesValue &&
    previousSelectedProfilesValue !== stringifiedSelectedProfilesIds;

  useEffect(() => {
    if (isParentDropdownExpanded) {
      scrollToFirstInvalid();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isParentDropdownExpanded]);

  if (shouldEnterScrollLogic) {
    const parsedPrevious: string[] = JSON.parse(previousSelectedProfilesValue);

    if (parsedPrevious.length + 1 === selectedProfiles.length) {
      // This is an edge case that we ignore. Otherwise it will open the dropdown
    } else if (parsedPrevious.length !== profiles.length && parsedPrevious.length <= selectedProfiles.length) {
      scrollToFirstInvalid();
    }
  }

  const { errors } = postGroup;

  const hasPinProfileWarning = hasSeverity(
    errors,
    ErrorMessageSeverity.warning,
    undefined,
    undefined,
    ['pinterestBoardRequired'],
    profile.id
  );

  const hasPinProfileError = hasSeverity(
    errors,
    ErrorMessageSeverity.error,
    undefined,
    undefined,
    ['pinterestBoardRequired'],
    profile.id
  );

  const profileError = hasPinProfileError ? 'Error' : '';
  const profileWarning = hasPinProfileWarning ? 'Warning' : '';

  return (
    <div className={'cell small-12'} onClick={e => e.stopPropagation()}>
      <div className={'grid-x margin-top-small-8'}>
        <div className={'cell small-12'}>
          {/* we set override to mouse to disable native select on touch devices, and make sure we always have dropdown */}
          <Select
            dropDownZIndex={`${+scssVars['z-index-overmodal'] + 1}`}
            overrideIntent={'mouse'}
            items={selectItems}
            dropdownTemplate={dropdownTemplateGenerator(refreshPnBoards, loading)}
            {...(pinterestBoardValue ? { selectedValue: pinterestBoardValue } : {})}
            chooseLabel={'Select a Pinterest Board'}
            hideDefaultSelectOption
            name={`${pinterestFormName}`}
            rules={pinterestRules}
            size={'medium'}
            useFormInstance={useFormInstance}
            {...(profileError || profileWarning
              ? {
                  errorMessage: ' ',
                  errorSeverity: profileError ? ErrorMessageSeverity.error : ErrorMessageSeverity.warning
                }
              : {})}
          />
        </div>
      </div>
    </div>
  );
};
